import React, { useEffect, useState, useCallback } from 'react';
import { fetchDashboardStats } from '../utils/contentApi';

// Reusable Performance Chart Component
const PerformanceChart = ({ title, subtitle, weeklyScores, selectedWeeks, chartHeight = 300 }) => {
  const formatWeekLabel = (weekDate) => {
    const date = new Date(weekDate);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  const maxScore = 100;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </div>

      {weeklyScores && weeklyScores.length > 0 ? (
        <div className="relative">
          {/* Chart */}
          <div className="flex items-end justify-between" style={{ height: chartHeight }}>
            {weeklyScores.map((week, index) => {
              const hasData = week.averageScore !== null;
              const barHeight = hasData
                ? (week.averageScore / maxScore) * (chartHeight - 40)
                : 0;

              return (
                <div
                  key={index}
                  className="flex-1 flex flex-col items-center justify-end px-1"
                >
                  {/* Bar */}
                  <div className="w-full flex flex-col items-center">
                    {hasData && (
                      <div className="text-xs font-semibold text-gray-700 mb-1">
                        {week.averageScore}%
                      </div>
                    )}
                    <div
                      className={`w-full rounded-t transition-all ${
                        hasData
                          ? week.averageScore >= 80
                            ? 'bg-green-500'
                            : week.averageScore >= 60
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                          : 'bg-gray-200'
                      }`}
                      style={{ height: `${barHeight}px`, minHeight: hasData ? '20px' : '4px' }}
                      title={
                        hasData
                          ? `Week of ${formatWeekLabel(week.week)}: ${week.averageScore}% (${week.sessionCount} ${week.sessionCount === 1 ? 'session' : 'sessions'})`
                          : `Week of ${formatWeekLabel(week.week)}: No data`
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-between mt-2 border-t border-gray-300 pt-2">
            {weeklyScores.map((week, index) => {
              const showLabel = selectedWeeks <= 12 || index % 2 === 0;
              return (
                <div
                  key={index}
                  className="flex-1 text-center px-1"
                >
                  {showLabel && (
                    <div className="text-xs text-gray-600 transform -rotate-45 origin-top-left mt-2">
                      {formatWeekLabel(week.week)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 flex justify-center gap-6 text-sm">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              <span className="text-gray-700">≥80%</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
              <span className="text-gray-700">60-79%</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
              <span className="text-gray-700">&lt;60%</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-200 rounded mr-2"></div>
              <span className="text-gray-700">No data</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-gray-600 p-4 bg-gray-50 rounded">
          No practice session data available yet. Complete some practice sessions to see your performance.
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeeks, setSelectedWeeks] = useState(12);
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDashboardStats(selectedWeeks, selectedLanguage);
      setStats(data);
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, [selectedWeeks, selectedLanguage]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  const practiceTypes = {
    'word-by-word': { title: 'Word-by-Word Practice', description: 'Standard word-by-word learning' },
    '5-min-quiz': { title: '5-Minute Quiz', description: 'Timed quiz with multiple choice' },
    'repeat-lower-50': { title: 'Repeat Lower 50%', description: 'Practice words with <50% success rate' }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {/* Words per Language Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4">Vocabulary Overview</h2>

        {stats.wordsPerLanguage && stats.wordsPerLanguage.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.wordsPerLanguage.map((lang, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {lang.language}
                </h3>
                <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                  <span className={`fi fi-${lang.nativeLanguageIso?.toLowerCase()}`}></span>
                  <span>{lang.nativeLanguage}</span>
                  <span className="mx-1">→</span>
                  <span className={`fi fi-${lang.foreignLanguageIso?.toLowerCase()}`}></span>
                  <span>{lang.foreignLanguage}</span>
                </div>
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-indigo-600">
                    {lang.wordCount}
                  </span>
                  <span className="ml-2 text-gray-600">
                    {lang.wordCount === 1 ? 'word' : 'words'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-600 p-4 bg-gray-50 rounded">
            No vocabulary data available yet. Add some words in the Content section.
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex justify-between items-center mb-6">
        {/* Language Filter - Left Side */}
        {stats.languages && stats.languages.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Language:</label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Languages</option>
                {stats.languages.map((lang) => (
                  <option key={lang._id} value={lang._id}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Flag indicators when a specific language is selected */}
            {selectedLanguage !== 'all' && (() => {
              const currentLang = stats.languages.find(lang => lang._id === selectedLanguage);
              return currentLang ? (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className={`fi fi-${currentLang.nativeLanguageIso?.toLowerCase()} text-lg`}></span>
                  <span>{currentLang.nativeLanguage}</span>
                  <span className="mx-1">→</span>
                  <span className={`fi fi-${currentLang.foreignLanguageIso?.toLowerCase()} text-lg`}></span>
                  <span>{currentLang.foreignLanguage}</span>
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* Time Range Selector - Right Side */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Time Range:</label>
          <div className="inline-flex rounded-md shadow-sm" role="group">
            {[4, 8, 12, 24].map((weeks) => (
              <button
                key={weeks}
                type="button"
                onClick={() => setSelectedWeeks(weeks)}
                className={`px-4 py-2 text-sm font-medium border transition-colors ${
                  selectedWeeks === weeks
                    ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } ${
                  weeks === 4
                    ? 'rounded-l-md'
                    : weeks === 24
                    ? 'rounded-r-md -ml-px'
                    : '-ml-px'
                }`}
              >
                {weeks}w
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overall Practice Performance */}
      <div className="mb-6">
        <PerformanceChart
          title="Overall Practice Performance"
          subtitle={`Average score per week (${stats.totalSessions} total sessions)`}
          weeklyScores={stats.weeklyScores}
          selectedWeeks={selectedWeeks}
          chartHeight={300}
        />
      </div>

      {/* Practice Type Specific Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {Object.entries(practiceTypes).map(([type, config]) => (
          <PerformanceChart
            key={type}
            title={config.title}
            subtitle={`${config.description} (${stats.sessionsByType?.[type] || 0} sessions)`}
            weeklyScores={stats.weeklyScoresByType?.[type] || []}
            selectedWeeks={selectedWeeks}
            chartHeight={250}
          />
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
